/**
 * Image Preview Component
 *
 * Displays image previews with upload progress, thumbnails, and description indicators.
 * Uses the unified ImageTile component for rendering.
 *
 * @module features/chat/components/preview/ImagePreview
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { generateStorageUrl, isStorageImageBlock } from "@/lib/storage";
import { useStorageContext } from "@/contexts/use-storage";
import { ImageTile } from "@/components/ui/ImageTile";
import type {
  Base64ContentBlock,
  StorageImageBlock,
} from "../../types/messages";
import { getImageFileName } from "../../types/messages";

// =============================================================================
// TYPES
// =============================================================================

export interface ImagePreviewProps {
  block: StorageImageBlock | Base64ContentBlock;
  removable?: boolean;
  onRemove?: () => void;
  onImageClick?: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  block,
  removable = false,
  onRemove,
  onImageClick,
  className,
  size = "md",
}) => {
  const { userId, conversationId } = useStorageContext();
  const { t } = useTranslation("chat");

  // Storage image block
  if (isStorageImageBlock(block)) {
    const isUploading = (block as StorageImageBlock & { uploading?: boolean }).uploading === true;
    const uploadProgress = (block as StorageImageBlock & { uploadProgress?: number }).uploadProgress;
    const isProcessing = (block as any).processing === true;
    const processingStage = (block as any).processingStage as "optimizing" | "validating" | "encoding" | "thumbnailing" | undefined;
    const file = (block as any).file as File | undefined;
    const hasDescription = Boolean(block.metadata?.description);

    const fileName = getImageFileName(block);

    // Use thumbnail for preview
    const thumbnail = block.thumbnail || block.metadata?.thumbnail;
    const url = thumbnail
      ? `data:image/jpeg;base64,${thumbnail}`
      : userId && conversationId && fileName
        ? generateStorageUrl("images", fileName, { userId, projectId: conversationId })
        : undefined;

    return (
      <ImageTile
        src={url}
        file={file}
        alt={String(block.metadata?.name || t("multimodal.uploadedImage"))}
        size={size}
        status={isUploading ? "uploading" : isProcessing ? "processing" : "idle"}
        progress={uploadProgress}
        processingStage={processingStage}
        badge={hasDescription ? "description" : undefined}
        onRemove={removable ? onRemove : undefined}
        onClick={!isUploading && !isProcessing ? onImageClick : undefined}
        className={cn(
          onImageClick && !isUploading && !isProcessing && "cursor-pointer hover:opacity-80 transition-opacity",
          className
        )}
      />
    );
  }

  // Base64 image block
  const base64Block = block as Base64ContentBlock;
  if (
    base64Block.type === "image" &&
    base64Block.source_type === "base64" &&
    typeof base64Block.mime_type === "string" &&
    base64Block.mime_type.startsWith("image/")
  ) {
    const imageData = base64Block.thumbnail || base64Block.data;
    const url = imageData ? `data:image/jpeg;base64,${imageData}` : undefined;
    const isUploading = (base64Block as Base64ContentBlock & { uploading?: boolean }).uploading === true;
    const uploadProgress = (base64Block as Base64ContentBlock & { uploadProgress?: number }).uploadProgress;
    const isProcessing = base64Block.processing === true;
    const processingStage = base64Block.processingStage;
    const file = base64Block.file;
    const hasDescription = Boolean(base64Block.metadata?.description);

    return (
      <ImageTile
        src={url}
        file={file}
        alt={String(base64Block.metadata?.name || t("multimodal.uploadedImage"))}
        size={size}
        status={isUploading ? "uploading" : isProcessing ? "processing" : "idle"}
        progress={uploadProgress}
        processingStage={processingStage}
        badge={hasDescription ? "description" : undefined}
        onRemove={removable ? onRemove : undefined}
        onClick={!isUploading && !isProcessing ? onImageClick : undefined}
        className={cn(
          onImageClick && !isUploading && !isProcessing && "cursor-pointer hover:opacity-80 transition-opacity",
          className
        )}
      />
    );
  }

  return null;
};
